import { client, deserializer } from './client'
import { snakeCase, camelCase } from 'change-case-object'

/**
 * Fetches entity merge records with pagination and filtering
 * @param {Object} params - The parameters for the request
 * @param {string} params.mergeType - The merge type ('PlatformPerson' | 'PlatformOrganization')
 * @param {number} params.pageSize - Number of records per page
 * @param {number} params.pageOffset - Offset for pagination
 * @param {string} [params.term] - Optional search term to filter by person name
 * @param {string} [params.status] - Optional status filter ('in_progress' | 'completed' | 'failed')
 *
 * @returns {Promise<Object>} Response object
 * @returns {Array<Object>} return.data - Deserialized merge records with relationships
 *   (user, sourcePerson, targetPerson, sourcePersonOrganization, targetPersonOrganization)
 * @returns {Object} return.meta - Pagination metadata
 * @returns {number} return.meta.itemCount - Total number of matching records
 */
async function getEntityMergeRecords(params) {
  const response = await client().get('/v2/entity_merge_records', {
    params: snakeCase(params),
  })

  return {
    data: deserializer.deserialize(response),
    meta: camelCase(response.meta),
  }
}

/**
 * Fetches a preview of the merged profile with job mappings applied
 * @param {string} sourceId - Platform person UUID
 * @param {string} targetId - FactSet person UUID
 * @param {Object} jobMappings - Job mapping object { sourceJobId: targetJobId }
 * @param {Array} jobOverrides - Array of job override objects
 *
 * @returns {Promise<Object>} Preview data
 * @returns {Object} return.data - Merged person data (camelCase)
 * @returns {Object} return.meta - Metadata including review info
 * @returns {boolean} return.meta.review.primaryJobChanged - Whether primary job changed
 * @returns {Object} return.meta.review.counts - Merge counts
 * @throws {Error} On validation error (400) or server error (5xx)
 * @description
 * **Success Response (200):**
 * ```json
 * {
 *   data: { ...mergedPersonData },
 *   meta: {
 *     review: {
 *       primaryJobChanged: boolean,
 *       counts: { ... }
 *     }
 *   }
 * }
 * ```
 *
 * **Error Response (400):**
 * ```json
 * {
 *   errors: [{ message: "...", field: "..." }]
 * }
 * ```
 * Note: Does NOT return a `success` field - check for data presence or catch errors.
 */
async function postPeopleMergeReview(sourceId, targetId, jobMappings, jobOverrides) {
  const response = await client().post('/v2/ds3/people/merge/review', {
    ...snakeCase({ sourceId, targetId, jobOverrides }),
    job_mappings: jobMappings, // Preserve UUID keys
  })

  const deserializedData = deserializer.deserialize(response)

  return {
    data: camelCase(deserializedData),
    meta: camelCase(response.meta),
  }
}

/**
 * Creates a people merge job (queues background job)
 * @param {string} sourceId - Platform person UUID (will be merged into target)
 * @param {string} targetId - FactSet person UUID (will receive merged data)
 * @param {Object} jobMappings - Job mapping object { sourceJobId: targetJobId }
 * @param {Array} jobOverrides - Array of job override objects
 *
 * @returns {Promise<Object>} Response object
 * @returns {boolean} return.success - Always `true` on success
 *
 * @throws {Error} On validation error (400) or server error (5xx)
 * @description
 * **Success Response (200):**
 * ```json
 * {
 *   "success": true
 * }
 * ```
 *
 * **Validation Error (400):**
 * ```json
 * {
 *   "errors": [
 *     { "message": "Invalid job mapping", "field": "job_mappings" }
 *   ]
 * }
 * ```
 *
 * **Server Error (500):**
 * Throws error - will be caught by React Query's error handling
 *
 * **Important:**
 * - Does NOT return `{ success: false }` on failure
 * - Failures throw errors or return error objects with 4xx/5xx status codes
 * - React Query automatically treats non-200 responses as errors
 */
async function createPeopleMerge(sourceId, targetId, jobMappings, jobOverrides) {
  const response = await client().post('/v2/ds3/people/merge', {
    ...snakeCase({ sourceId, targetId, jobOverrides }),
    job_mappings: jobMappings, // Preserve UUID keys
  })

  return response
}

export { getEntityMergeRecords, postPeopleMergeReview, createPeopleMerge }
